import React, { useState, useEffect, useCallback } from "react";
import {
  Users, User, Search, Plus, ChevronLeft, X, Edit2, Trash2,
  Save, RefreshCw, BookOpen, Award, Briefcase, Download
} from "lucide-react";
import { exportCSV, printTable } from '../../../utils/exportHelper';
import { Card, Btn, Input, Field, Pagination } from "../../../components/UI";
import { useToast } from "../../../components/Toast";
import { personelAPI } from "../../../utils/api";

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day:"2-digit", month:"short", year:"numeric" }) : "—";

const PANGKAT_OPTIONS = [
  "JENDERAL","LETJEN","MAYJEN","BRIGJEN",
  "KOLONEL","LETKOL","MAYOR",
  "KAPTEN","LETTU","LETDA",
  "PEMBANTU LETNAN SATU","PEMBANTU LETNAN DUA",
  "SERMA","SERKA","SERTU","SERDA",
  "KOPKA","KOPTU","KOPDA",
  "PRAKA","PRATU","PRADA",
];

const AGAMA_OPTIONS = ["Islam","Kristen Protestan","Kristen Katolik","Hindu","Budha","Konghucu"];
const STATUS_KAWIN_OPTIONS = ["Belum Kawin","Kawin","Cerai Hidup","Cerai Mati"];
const GOLDAR_OPTIONS = ["A","B","AB","O","A+","B+","AB+","O+"];

// ── Page-specific shared UI ────────────────────────────────────────────────

const selStyle = {
  width:"100%", padding:"8px 12px", border:"1.5px solid #E5E7EB", borderRadius:8,
  fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit",
  background:"white",
};

const PangkatBadge = ({ pangkat }) => {
  const isJenderal = /JENDERAL|BRIGJEN|MAYJEN|LETJEN/.test(pangkat || "");
  const isPerwira  = /KOLONEL|LETKOL|MAYOR|KAPTEN|LETTU|LETDA|PEM/.test(pangkat || "");
  const bg = isJenderal ? "#7C3AED" : isPerwira ? "#2563EB" : "#059669";
  return (
    <span style={{ background:`${bg}20`, color:bg, padding:"2px 8px",
      borderRadius:20, fontSize:11, fontWeight:700 }}>
      {pangkat || "—"}
    </span>
  );
};

const PERSONIL_FORM_EMPTY = {
  nrp:"", gelar_depan:"", nama:"", gelar_belakang:"", tempat_lahir:"", tanggal_lahir:"",
  pangkat:"", corps:"", kesatuan:"", jabatan:"", tmt_tni:"", tmt_jabatan:"",
  agama:"", status_kawin:"", golongan_darah:"", kotama_id:"", satminkal_id:"",
  jenis_rambut_id:"", warna_kulit_id:"", foto:"",
};

// ── RIWAYAT PANEL ─────────────────────────────────────────────────────────

const tdStyle = { padding:"8px 10px", color:"#374151" };

const RiwayatPanel = ({ personel, onUpdate, showToast }) => {
  const [tab, setTab]      = useState("dikum");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({});

  const TABS = [
    { id:"dikum",   label:"Riwayat Dikum",   icon:<BookOpen size={13}/> },
    { id:"dikmil",  label:"Riwayat Dikmil",  icon:<Award    size={13}/> },
    { id:"jabatan", label:"Riwayat Jabatan", icon:<Briefcase size={13}/> },
  ];

  const addRiwayat = async () => {
    try {
      if (tab === "dikum")   await personelAPI.addDikum(personel.nrp, addForm);
      if (tab === "dikmil")  await personelAPI.addDikmil(personel.nrp, addForm);
      if (tab === "jabatan") await personelAPI.addJabatan(personel.nrp, addForm);
      showToast("Riwayat ditambahkan");
      setShowAdd(false); setAddForm({});
      onUpdate();
    } catch(e) { showToast(e.message, "error"); }
  };

  const delRiwayat = async (id) => {
    if (!window.confirm("Hapus riwayat ini?")) return;
    try {
      if (tab === "dikum")   await personelAPI.delDikum(id);
      if (tab === "dikmil")  await personelAPI.delDikmil(id);
      if (tab === "jabatan") await personelAPI.delJabatan(id);
      showToast("Riwayat dihapus");
      onUpdate();
    } catch(e) { showToast(e.message, "error"); }
  };

  const rows = tab === "dikum"   ? personel.riwayat_dikum
             : tab === "dikmil"  ? personel.riwayat_dikmil
             : personel.riwayat_jabatan;

  return (
    <Card style={{ padding:0, overflow:"hidden" }}>
      <div style={{ display:"flex", borderBottom:"1px solid #E5E7EB", background:"#F9FAFB" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setShowAdd(false); setAddForm({}); }}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"12px 16px",
              border:"none", background:"none", cursor:"pointer",
              fontSize:12, fontWeight: tab===t.id ? 700 : 500,
              color: tab===t.id ? "#2563EB" : "#6B7280",
              borderBottom: tab===t.id ? "2px solid #2563EB" : "2px solid transparent",
              fontFamily:"inherit" }}>
            {t.icon}{t.label}
          </button>
        ))}
        <div style={{ flex:1 }}/>
        <button onClick={() => { setShowAdd(!showAdd); setAddForm({}); }}
          style={{ margin:"8px 12px", display:"flex", alignItems:"center", gap:4,
            background:"#EFF6FF", color:"#2563EB", border:"none", borderRadius:8,
            padding:"6px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
          <Plus size={13}/> Tambah
        </button>
      </div>

      {showAdd && (
        <div style={{ padding:16, borderBottom:"1px solid #E5E7EB", background:"#F8FAFF" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
            {tab === "dikum" && <>
              <Input placeholder="Jenis Pendidikan" value={addForm.jenis_pendidikan||""} onChange={e => setAddForm(f=>({...f,jenis_pendidikan:e.target.value}))}/>
              <Input placeholder="Tahun" type="number" value={addForm.tahun||""} onChange={e => setAddForm(f=>({...f,tahun:e.target.value}))}/>
              <Input placeholder="Nama Pendidikan" value={addForm.nama_pendidikan||""} onChange={e => setAddForm(f=>({...f,nama_pendidikan:e.target.value}))}/>
            </>}
            {tab === "dikmil" && <>
              <Input placeholder="Jenis Pendidikan" value={addForm.jenis_pendidikan||""} onChange={e => setAddForm(f=>({...f,jenis_pendidikan:e.target.value}))}/>
              <Input placeholder="Tahun" type="number" value={addForm.tahun||""} onChange={e => setAddForm(f=>({...f,tahun:e.target.value}))}/>
              <Input placeholder="Nama Pendidikan" value={addForm.nama_pendidikan||""} onChange={e => setAddForm(f=>({...f,nama_pendidikan:e.target.value}))}/>
            </>}
            {tab === "jabatan" && <>
              <Input placeholder="Jabatan" value={addForm.jabatan||""} onChange={e => setAddForm(f=>({...f,jabatan:e.target.value}))}/>
              <Input placeholder="Kesatuan" value={addForm.kesatuan||""} onChange={e => setAddForm(f=>({...f,kesatuan:e.target.value}))}/>
              <Input placeholder="TMT (tgl)" type="date" value={addForm.tmt||""} onChange={e => setAddForm(f=>({...f,tmt:e.target.value}))}/>
            </>}
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="ghost" size="sm" onClick={() => { setShowAdd(false); setAddForm({}); }}>Batal</Btn>
            <Btn size="sm" icon={<Save size={12}/>} onClick={addRiwayat}>Simpan</Btn>
          </div>
        </div>
      )}

      <div style={{ padding:16 }}>
        {rows.length === 0 ? (
          <p style={{ textAlign:"center", color:"#9CA3AF", fontSize:13, margin:20 }}>Belum ada data riwayat</p>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid #E5E7EB" }}>
                {tab === "dikum" && ["Jenis Pendidikan","Tahun","Nama Pendidikan","Prestasi",""].map(h =>
                  <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6B7280" }}>{h}</th>)}
                {tab === "dikmil" && ["Jenis Pendidikan","Tahun","Nama Pendidikan","Pangkat",""].map(h =>
                  <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6B7280" }}>{h}</th>)}
                {tab === "jabatan" && ["Jabatan","Kesatuan","TMT","TMT Selesai","No.Kep",""].map(h =>
                  <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6B7280" }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom:"1px solid #F3F4F6" }}>
                  {tab === "dikum" && <>
                    <td style={tdStyle}>{r.jenis_pendidikan||"—"}</td>
                    <td style={tdStyle}>{r.tahun||"—"}</td>
                    <td style={tdStyle}>{r.nama_pendidikan||"—"}</td>
                    <td style={tdStyle}>{r.prestasi||"—"}</td>
                  </>}
                  {tab === "dikmil" && <>
                    <td style={tdStyle}>{r.jenis_pendidikan||"—"}</td>
                    <td style={tdStyle}>{r.tahun||"—"}</td>
                    <td style={tdStyle}>{r.nama_pendidikan||"—"}</td>
                    <td style={tdStyle}>{r.pangkat_saat_itu||"—"}</td>
                  </>}
                  {tab === "jabatan" && <>
                    <td style={tdStyle}>{r.jabatan||"—"}</td>
                    <td style={tdStyle}>{r.kesatuan||"—"}</td>
                    <td style={tdStyle}>{fmtDate(r.tmt)}</td>
                    <td style={tdStyle}>{fmtDate(r.tmt_selesai)}</td>
                    <td style={tdStyle}>{r.no_kep||"—"}</td>
                  </>}
                  <td style={{ padding:"8px 10px" }}>
                    <button onClick={() => delRiwayat(r.id)}
                      style={{ background:"none", border:"none", cursor:"pointer", color:"#EF4444", padding:2 }}>
                      <Trash2 size={13}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
};

// ── PERSONIL TAB ──────────────────────────────────────────────────────────

const PersonilTab = ({ kotamaList, satminkalList, rambutList, kulitList }) => {
  const [list,     setList]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [view,     setView]     = useState("list");
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState(PERSONIL_FORM_EMPTY);
  const [isEdit,   setIsEdit]   = useState(false);
  const { toast: showToast } = useToast();

  const LIMIT = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `?page=${page}&limit=${LIMIT}${search ? `&search=${encodeURIComponent(search)}` : ""}`;
      const res = await personelAPI.getAll(qs);
      setList(res.data);
      setTotal(res.pagination?.total || 0);
    } catch(e) { showToast(e.message, "error"); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (nrp) => {
    try {
      const res = await personelAPI.getById(nrp);
      setSelected(res.data);
      setView("detail");
    } catch(e) { showToast(e.message, "error"); }
  };

  const openForm = (p = null) => {
    if (p) {
      setForm({ ...PERSONIL_FORM_EMPTY, ...p,
        tanggal_lahir: p.tanggal_lahir?.split("T")[0] || "",
        tmt_tni:       p.tmt_tni?.split("T")[0] || "",
        tmt_jabatan:   p.tmt_jabatan?.split("T")[0] || "",
      });
      setIsEdit(true);
    } else {
      setForm(PERSONIL_FORM_EMPTY);
      setIsEdit(false);
    }
    setView("form");
  };

  const saveForm = async () => {
    if (!form.nrp || !form.nama) { showToast("NRP dan Nama wajib diisi", "error"); return; }
    try {
      if (isEdit) {
        await personelAPI.update(form.nrp, form);
        showToast("Personel diperbarui");
      } else {
        await personelAPI.create(form);
        showToast("Personel ditambahkan");
      }
      setView("list"); load();
    } catch(e) { showToast(e.message, "error"); }
  };

  const deletePersonel = async (nrp) => {
    if (!window.confirm("Hapus personel ini?")) return;
    try {
      await personelAPI.delete(nrp);
      showToast("Personel dihapus");
      if (view === "detail") setView("list");
      load();
    } catch(e) { showToast(e.message, "error"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // ── LIST VIEW ──
  if (view === "list") return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700, color:"#111827" }}>Data Personil</h2>
          <p style={{ margin:"2px 0 0", fontSize:12, color:"#6B7280" }}>{total} personel terdaftar</p>
        </div>
        <Btn icon={<Plus size={14}/>} onClick={() => openForm()}>Tambah Personel</Btn>
      </div>

      <Card style={{ marginBottom:16 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ position:"relative", flex:1 }}>
            <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#9CA3AF" }}/>
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Cari NRP atau Nama…" style={{ paddingLeft:32 }}/>
          </div>
          <Btn variant="ghost" icon={<RefreshCw size={13}/>} onClick={load} size="md">Refresh</Btn>
          <Btn variant="ghost" icon={<Download size={13}/>} size="md" onClick={() => {
            const headers = ['NRP','Nama','Pangkat','Corps','Jabatan','Kotama','Status'];
            const rows = list.map(p => [p.nrp, p.nama, p.pangkat, p.corps, p.jabatan, p.kotama_nama || p.kesatuan, p.status]);
            exportCSV('personel', headers, rows);
          }}>CSV</Btn>
          <Btn variant="ghost" size="md" onClick={() => {
            const headers = ['NRP','Nama','Pangkat','Corps','Jabatan','Kotama','Status'];
            const rows = list.map(p => [p.nrp, p.nama, p.pangkat, p.corps, p.jabatan, p.kotama_nama || p.kesatuan, p.status]);
            printTable('Data Personel', headers, rows);
          }}>Print</Btn>
        </div>
      </Card>

      <Card style={{ padding:0, overflow:"hidden" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:"#9CA3AF", fontSize:13 }}>Memuat data…</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign:"center", padding:40, color:"#9CA3AF", fontSize:13 }}>
            <Users size={32} style={{ marginBottom:8, opacity:0.4 }}/><br/>Belum ada data personel
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid #E5E7EB", background:"#F9FAFB" }}>
                {["NRP","Nama","Pangkat","Corps","Jabatan","Kotama","Aksi"].map(h => (
                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11,
                    fontWeight:700, color:"#6B7280", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((p, i) => (
                <tr key={p.nrp} style={{ borderBottom:"1px solid #F3F4F6",
                  background: i%2===0 ? "white" : "#FAFAFA" }}>
                  <td style={{ padding:"10px 14px" }}>
                    <button onClick={() => openDetail(p.nrp)}
                      style={{ background:"none", border:"none", cursor:"pointer",
                        color:"#2563EB", fontWeight:700, fontSize:13, padding:0, fontFamily:"inherit" }}>
                      {p.nrp}
                    </button>
                  </td>
                  <td style={{ padding:"10px 14px", fontWeight:500, color:"#111827" }}>
                    {[p.gelar_depan, p.nama, p.gelar_belakang].filter(Boolean).join(" ")}
                  </td>
                  <td style={{ padding:"10px 14px" }}><PangkatBadge pangkat={p.pangkat}/></td>
                  <td style={{ padding:"10px 14px", color:"#6B7280" }}>{p.corps || "—"}</td>
                  <td style={{ padding:"10px 14px", color:"#6B7280", maxWidth:200,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.jabatan || "—"}</td>
                  <td style={{ padding:"10px 14px", color:"#6B7280" }}>{p.kotama || "—"}</td>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", gap:6 }}>
                      <Btn size="sm" variant="ghost" icon={<Edit2 size={12}/>}
                        onClick={() => openForm(p)}>Edit</Btn>
                      <Btn size="sm" variant="danger" icon={<Trash2 size={12}/>}
                        onClick={() => deletePersonel(p.nrp)}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && (
          <div style={{ padding:"0 16px" }}>
            <Pagination page={page} totalPages={totalPages} total={total} from={total===0?0:(page-1)*LIMIT+1} to={Math.min(page*LIMIT,total)} pageSize={LIMIT} onPageChange={setPage} onPageSizeChange={() => {}} />
          </div>
        )}
      </Card>
    </>
  );

  // ── DETAIL VIEW ──
  if (view === "detail" && selected) return (
    <>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <Btn variant="ghost" icon={<ChevronLeft size={14}/>} onClick={() => setView("list")}>Kembali</Btn>
        <div style={{ flex:1 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700, color:"#111827" }}>{selected.nama}</h2>
          <p style={{ margin:0, fontSize:12, color:"#6B7280" }}>NRP: {selected.nrp}</p>
        </div>
        <Btn icon={<Edit2 size={13}/>} onClick={() => openForm(selected)}>Edit Data</Btn>
        <Btn variant="danger" icon={<Trash2 size={13}/>} onClick={() => deletePersonel(selected.nrp)}>Hapus</Btn>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"300px 1fr", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <Card>
            <div style={{ width:100, height:120, background:"#E5E7EB", borderRadius:8,
              margin:"0 auto 16px", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {selected.foto
                ? <img src={selected.foto} alt="foto" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:8 }}/>
                : <User size={40} color="#9CA3AF"/>}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                ["NRP",        selected.nrp],
                ["Pangkat",    <PangkatBadge pangkat={selected.pangkat}/>],
                ["Corps",      selected.corps],
                ["Jabatan",    selected.jabatan],
                ["Kesatuan",   selected.kesatuan],
                ["TMT TNI",    fmtDate(selected.tmt_tni)],
                ["TMT Jabatan",fmtDate(selected.tmt_jabatan)],
              ].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:8 }}>
                  <span style={{ fontSize:11, color:"#6B7280", fontWeight:600, whiteSpace:"nowrap" }}>{k}</span>
                  <span style={{ fontSize:12, color:"#111827", textAlign:"right" }}>{v || "—"}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <h4 style={{ margin:"0 0 10px", fontSize:12, fontWeight:700, color:"#374151" }}>Data Diri</h4>
            {[
              ["Tgl Lahir",    fmtDate(selected.tanggal_lahir)],
              ["Tempat Lahir", selected.tempat_lahir],
              ["Agama",        selected.agama],
              ["Status Kawin", selected.status_kawin],
              ["Gol. Darah",   selected.golongan_darah],
              ["Jenis Rambut", selected.jenis_rambut_nama],
              ["Warna Kulit",  selected.warna_kulit_nama],
              ["Kotama",       selected.kotama_nama],
              ["Satminkal",    selected.satminkal_nama],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:6 }}>
                <span style={{ fontSize:11, color:"#6B7280", fontWeight:600 }}>{k}</span>
                <span style={{ fontSize:12, color:"#111827" }}>{v || "—"}</span>
              </div>
            ))}
          </Card>
        </div>

        <RiwayatPanel personel={selected} onUpdate={() => openDetail(selected.nrp)} showToast={showToast}/>
      </div>
    </>
  );

  // ── FORM VIEW ──
  if (view === "form") return (
    <>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <Btn variant="ghost" icon={<ChevronLeft size={14}/>} onClick={() => setView(isEdit ? "detail" : "list")}>Batal</Btn>
        <h2 style={{ margin:0, fontSize:16, fontWeight:700, color:"#111827" }}>
          {isEdit ? "Edit Personel" : "Tambah Personel Baru"}
        </h2>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <Card>
          <h4 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#374151" }}>Identitas</h4>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <Field label="NRP" required>
              <Input value={form.nrp} onChange={e => setForm(f => ({...f, nrp:e.target.value}))}
                placeholder="Nomor Registrasi Pokok" disabled={isEdit}
                style={isEdit ? {background:"#F3F4F6",color:"#9CA3AF"} : {}}/>
            </Field>
            <Field label="Gelar Depan">
              <Input value={form.gelar_depan} onChange={e => setForm(f => ({...f, gelar_depan:e.target.value}))} placeholder="dr., Ir., S.E., dll"/>
            </Field>
            <Field label="Nama Lengkap" required>
              <Input value={form.nama} onChange={e => setForm(f => ({...f, nama:e.target.value}))} placeholder="Nama tanpa gelar"/>
            </Field>
            <Field label="Gelar Belakang">
              <Input value={form.gelar_belakang} onChange={e => setForm(f => ({...f, gelar_belakang:e.target.value}))} placeholder="M.M., S.H., Ph.D., dll"/>
            </Field>
            <Field label="Tempat Lahir">
              <Input value={form.tempat_lahir} onChange={e => setForm(f => ({...f, tempat_lahir:e.target.value}))} placeholder="Kota lahir"/>
            </Field>
            <Field label="Tanggal Lahir">
              <Input type="date" value={form.tanggal_lahir} onChange={e => setForm(f => ({...f, tanggal_lahir:e.target.value}))}/>
            </Field>
            <Field label="Agama">
              <select value={form.agama} onChange={e => setForm(f => ({...f, agama:e.target.value}))} style={selStyle}>
                <option value="">— Pilih —</option>
                {AGAMA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Status Kawin">
              <select value={form.status_kawin} onChange={e => setForm(f => ({...f, status_kawin:e.target.value}))} style={selStyle}>
                <option value="">— Pilih —</option>
                {STATUS_KAWIN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Golongan Darah">
              <select value={form.golongan_darah} onChange={e => setForm(f => ({...f, golongan_darah:e.target.value}))} style={selStyle}>
                <option value="">— Pilih —</option>
                {GOLDAR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Field label="Jenis Rambut">
                <select value={form.jenis_rambut_id} onChange={e => setForm(f => ({...f, jenis_rambut_id:e.target.value}))} style={selStyle}>
                  <option value="">— Pilih —</option>
                  {rambutList.map(r => <option key={r.id} value={r.id}>{r.nama}</option>)}
                </select>
              </Field>
              <Field label="Warna Kulit">
                <select value={form.warna_kulit_id} onChange={e => setForm(f => ({...f, warna_kulit_id:e.target.value}))} style={selStyle}>
                  <option value="">— Pilih —</option>
                  {kulitList.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </Card>

        <Card>
          <h4 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#374151" }}>Kepangkatan & Jabatan</h4>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <Field label="Pangkat">
              <select value={form.pangkat} onChange={e => setForm(f => ({...f, pangkat:e.target.value}))} style={selStyle}>
                <option value="">— Pilih Pangkat —</option>
                {PANGKAT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Corps / Kecabangan">
              <Input value={form.corps} onChange={e => setForm(f => ({...f, corps:e.target.value}))} placeholder="INF, CAV, ARH, dll"/>
            </Field>
            <Field label="Jabatan">
              <Input value={form.jabatan} onChange={e => setForm(f => ({...f, jabatan:e.target.value}))} placeholder="Jabatan saat ini"/>
            </Field>
            <Field label="Kesatuan">
              <Input value={form.kesatuan} onChange={e => setForm(f => ({...f, kesatuan:e.target.value}))} placeholder="Nama kesatuan"/>
            </Field>
            <Field label="TMT TNI">
              <Input type="date" value={form.tmt_tni} onChange={e => setForm(f => ({...f, tmt_tni:e.target.value}))}/>
            </Field>
            <Field label="TMT Jabatan">
              <Input type="date" value={form.tmt_jabatan} onChange={e => setForm(f => ({...f, tmt_jabatan:e.target.value}))}/>
            </Field>
            <Field label="Kotama">
              <select value={form.kotama_id} onChange={e => setForm(f => ({...f, kotama_id:e.target.value, satminkal_id:""}))} style={selStyle}>
                <option value="">— Pilih Kotama —</option>
                {kotamaList.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
              </select>
            </Field>
            <Field label="Satminkal">
              <select value={form.satminkal_id} onChange={e => setForm(f => ({...f, satminkal_id:e.target.value}))} style={selStyle}>
                <option value="">— Pilih Satminkal —</option>
                {satminkalList
                  .filter(s => !form.kotama_id || s.kotama_id === form.kotama_id)
                  .map(s => <option key={s.id} value={s.id}>{s.nama}</option>)}
              </select>
            </Field>
          </div>
        </Card>
      </div>

      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end", gap:10 }}>
        <Btn variant="ghost" onClick={() => setView(isEdit ? "detail" : "list")}>Batal</Btn>
        <Btn icon={<Save size={14}/>} onClick={saveForm}>{isEdit ? "Simpan Perubahan" : "Tambah Personel"}</Btn>
      </div>
    </>
  );

  return null;
};

export default PersonilTab;
